/* ----------------------------------------------------------------------------------------------------
 * BACKORDER PROPERTY — <backorder-property> custom element
 *
 * Attaches a line-item property `Availability: Backorder — ships in 1–10 days` to a product line when the
 * selected variant is a backorder (continue-selling + out of stock). This makes the backorder state visible
 * in the Shopify ADMIN order, on the packing slip, and in the order confirmation email — the storefront's
 * live inventory note (snippets/inventory.liquid + line-item.liquid) never persisted to the order.
 *
 * How it works:
 *   - The enclosing product form carries a hidden <input name="properties[Availability]"> that is `disabled`
 *     unless the currently-selected variant is a backorder. A disabled input is NOT serialized, so in-stock
 *     variants add with no property.
 *   - Liquid sets the correct initial `disabled` state for the first/selected variant (so single-variant
 *     products need no JS at all). For multi-variant products we recompute at add-to-cart time.
 *   - We listen for `submit` in the CAPTURE phase on `document` (an ancestor of the form) so our recompute
 *     runs BEFORE Impact's <product-form> bubble-phase handler serializes the form. We read the resolved
 *     variant id straight off the form (FormData(form).get('id')), which <product-form> keeps current.
 *
 * EE build 2026-07-23. Paired with snippets/backorder-property.liquid.
 * -------------------------------------------------------------------------------------------------- */

if (!customElements.get('backorder-property')) {
  class BackorderProperty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="properties[Availability]"]');
      if (!this.input) return;

      this.ids = new Set((this.dataset.ids || '').split(',').map((s) => s.trim()).filter(Boolean));

      const formId = this.getAttribute('form-id');
      this.form = formId ? document.getElementById(formId) : this.closest('form');
      if (!this.form) return;

      this._onSubmit = this._onSubmit.bind(this);
      document.addEventListener('submit', this._onSubmit, true);
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this._onSubmit, true);
    }

    _onSubmit(event) {
      if (event.target !== this.form) return;
      let id = '';
      try {
        id = String(new FormData(this.form).get('id') || '');
      } catch (err) {
        /* if we can't read the form, leave the Liquid-set initial state as-is */
        return;
      }
      if (!id) return;
      this.input.disabled = !this.ids.has(id);
    }
  }

  customElements.define('backorder-property', BackorderProperty);
}
